/**
 * ARIA In Progress Jobs Page
 * Version: 1.0.0
 *
 * ARIA-5: In Progress (exclude estimates, show ready-to-go unscheduled only)
 */

import { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { Clock, MapPin, User, Wrench } from 'lucide-react';
import type { Job } from '../types';
import { IdentifierBadge } from '../components/jobs/IdentifierBadge';
import { JobDetailsModal } from '../components/jobs/JobDetailsModal';

const IN_PROGRESS_JOBS_QUERY = gql`
  query InProgressJobs($excludeEstimates: Boolean) {
    jobsInProgress(excludeEstimates: $excludeEstimates) {
      id
      sytelineOrderNumber
      seNumber
      customerName
      customerPhone
      fullAddress
      city
      jobType
      identifierType
      scheduling {
        scheduledDate
        startTime
        appointmentDescription
      }
      technician {
        id
        fullName
        phone
      }
      jobSummary
      description
    }
  }
`;

export default function InProgressJobs() {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  // ARIA-5: Exclude estimates by default
  const { data, loading, refetch } = useQuery(IN_PROGRESS_JOBS_QUERY, {
    variables: { excludeEstimates: true },
    pollInterval: 30000, // Refresh every 30 seconds
  });

  const jobs = (data?.jobsInProgress || []) as Job[];

  const handleViewDetails = (jobId: string) => {
    setSelectedJobId(jobId);
  };

  const handleCloseModal = () => {
    setSelectedJobId(null);
  };

  const handleJobCompleted = () => {
    refetch();
  };

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">In Progress Jobs</h1>
        <p className="text-gray-600">
          Currently active service jobs (excluding estimates)
        </p>
      </header>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading in-progress jobs...</div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No jobs currently in progress</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map((job) => (
            <InProgressJobCard
              key={job.id}
              job={job}
              onViewDetails={() => handleViewDetails(job.id)}
            />
          ))}
        </div>
      )}

      {/* Job Details Modal */}
      <JobDetailsModal
        jobId={selectedJobId || ''}
        isOpen={!!selectedJobId}
        onClose={handleCloseModal}
        onJobCompleted={handleJobCompleted}
      />
    </div>
  );
}

interface InProgressJobCardProps {
  job: Job;
  onViewDetails: () => void;
}

function InProgressJobCard({ job, onViewDetails }: InProgressJobCardProps) {
  const startTime = job.scheduling?.startTime
    ? new Date(job.scheduling.startTime)
    : null;

  const elapsed = startTime
    ? Math.floor((Date.now() - startTime.getTime()) / (1000 * 60))
    : null;

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">
              {job.seNumber || job.sytelineOrderNumber}
            </span>
            <IdentifierBadge type={job.identifierType} />
          </div>
          <p className="text-sm text-gray-600">{job.customerName}</p>
        </div>
        <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
          <Wrench className="w-3 h-3" />
          In Progress
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-gray-600">
          <MapPin className="w-4 h-4 text-gray-400" />
          <span>{job.fullAddress}</span>
        </div>

        <div className="flex items-center gap-2 text-gray-600">
          <User className="w-4 h-4 text-gray-400" />
          <span>{job.technician?.fullName || 'Unassigned'}</span>
        </div>

        {startTime && (
          <div className="flex items-center gap-2 text-gray-600">
            <Clock className="w-4 h-4 text-gray-400" />
            <span>
              Started {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              {elapsed !== null && (
                <span className="ml-1 text-gray-400">
                  ({elapsed} min ago)
                </span>
              )}
            </span>
          </div>
        )}
      </div>

      {(job.scheduling?.appointmentDescription || job.jobSummary) && (
        <p className="mt-3 text-sm text-gray-500 border-t border-gray-100 pt-3">
          {job.scheduling?.appointmentDescription || job.jobSummary}
        </p>
      )}

      <div className="mt-3 flex gap-2">
        <button onClick={onViewDetails} className="btn-secondary text-xs flex-1">
          View Details
        </button>
        <button onClick={onViewDetails} className="btn-primary text-xs flex-1">
          Complete Job
        </button>
      </div>
    </div>
  );
}
